import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS, USERTYPE } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";

/**
 * Per-module "home" navigation, shared by the app-switcher menu (AppMenu) and the
 * upper-left brand logo (AppToolbar) so a logo click and the menu land on the same
 * place. Extracted from AppMenu so the two entry points can't drift apart, giving
 * Aspen/Birch the same click-the-logo-goes-home behavior Cedar already has.
 */
export const useModuleHome = () => {
    const navigate = useNavigate();
    const { currentUser, corporations, rcaEnabled } = useContext(CorporateContext);

    // Go to a mill's dashboards on the Live view. The `resetToLive` nav state tells
    // MillDashboard to snap the report dropdown back to Live even when the user was
    // already on the dashboards tab looking at Summary/Trend/Details (the dropdown is
    // component-local state, so navigating to the same mill URL wouldn't reset it on
    // its own). The explicit #dashboards hash keeps Locations from replace-redirecting
    // an empty hash and dropping the nav state.
    const goToMillHome = (millId: number | string) => {
        navigate(
            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${millId}#${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.HASH_PATHS.DASHBOARDS}`,
            { state: { resetToLive: true } },
        );
    };

    // Aspen (Downtime) home — role-aware: admins/executives land on the corporate
    // dashboard (or the single mill's Live view when the corp has just one), everyone
    // else on their own mill's Live view.
    const goToAspenHome = () => {
        if (
            currentUser?.user_type.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type.user_type_id === USERTYPE.EXECUTIVE ||
            currentUser?.user_type.user_type_id === USERTYPE.OSORNOADMINISTRATOR
        ) {
            if (corporations[0]?.mills?.length === 1) {
                goToMillHome(corporations[0]?.mills[0].mill_id);
            } else {
                navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.CORPORATE_DASHBOARD}`);
            }
        } else {
            goToMillHome(currentUser?.mill?.mill_id || 0);
        }
    };

    // Birch (RCA/AP) home — only meaningful when RCA is enabled for the corp.
    const goToRCAHome = () => {
        if (rcaEnabled) {
            navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`);
        }
    };

    // Route a brand-logo click by the active top-bar module.
    const goToModuleHome = (moduleName: "Aspen" | "Birch") => {
        if (moduleName === "Birch") {
            goToRCAHome();
        } else {
            goToAspenHome();
        }
    };

    return { goToAspenHome, goToRCAHome, goToModuleHome };
};

export default useModuleHome;
